import { FormFieldContext, FormFieldOperations, InitialCell, ValueGroupsByDate } from "contexts/FormFieldContext";
import { FC, MouseEvent, useContext, useMemo, useState } from "react";
import produce from "immer";
import {
  Block,
  BlockDate,
  Cell,
  CellKey,
  CellType,
  CellValue,
  DeleteReferencesDocument,
  DeleteValueGroupsDocument,
  FieldCellValue,
  FieldValueGroupIds,
  FieldValueGroupReference,
  FieldValueGroupSelectionStatus,
  FieldValueGroupTag,
  Form,
  FormField,
  SortFieldValueGroupsDocument,
  UpdateFormCaseValueGroupArgsInput,
  UpdateValueGroupInfoBulkDocument,
  UpsertFullValueGroupsDocument,
  UpsertValueGroupReferenceDocument,
  ValueGroupToAddInput,
} from "generated/graphql";
import {
  cellValueFromGQL,
  FieldCellValueUI,
  formatDateBackend,
  FormFieldValueGroupUI,
  FormFieldValuesUI,
  gqlCellValueFromValue,
  parseBlockDate,
  RowViewState,
  UNGROUPED_CELL_ID,
} from "Backend";
import { arrayIndexHandle, objectPropertyHandle, StateHandle } from "StateHandle";
import { empty, nonEmpty, pluralize } from "helpers/coll";
import { runGqlMutation } from "gqlClient";
import { getCellValidationErrorsForValue, validateCellValue } from "helpers/FormHelpers";
import { useSnackbar } from "notistack";
import { valueGroupDateValues } from "components/FormField/form-field-helpers";
import { CaseFormContext } from "contexts/CaseFormContext";
import { P } from "theme/typography";
import { UsePopover } from "helpers/hooks/usePopover";
import { displaySimpleCellValueString } from "components/FormField/displayCellValue";
import truncateString from "helpers/truncateString";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import { useAppSelector } from "redux/hooks";
import { selectFieldTagConfigs, selectTags } from "redux/slices/clientLens";
import { STARRED, UNTAGGED } from "components/tags/constants";

const EMPTY_VALUE_GROUP_ID = -1;
function emptyValueGroup(
  field: FormField,
  id: number,
  blockDate?: BlockDate,
  initialCell?: { cellId: number; value: CellValue }
): FormFieldValueGroupUI {
  const cells: FieldCellValue[] = field.cells.map((c) => {
    if (initialCell?.cellId === c.id) {
      return {
        cellId: c.id,
        valueGroupIndex: id,
        value: gqlCellValueFromValue(c.type, cellValueFromGQL(initialCell.value)),
      };
    }

    const value: CellValue =
      gqlCellValueFromValue(
        c.type,
        blockDate ? formatDateBackend(parseBlockDate(blockDate)) : cellValueFromGQL(c.default)
      ) || {};
    return {
      cellId: c.id,
      valueGroupIndex: id,
      value: value,
    };
  });
  return {
    id,
    localGroupId: id,
    rank: id,
    fieldId: field.id,
    values: cells,
    isNew: true,
    notes: "",
    manual: false,
    serialId: EMPTY_VALUE_GROUP_ID,
    references: [],
    selectionStatus: FieldValueGroupSelectionStatus.Default,
    viewState: RowViewState.EditExpanded,
    responseNumber: -1,
    tags: [],
    availableTags: [],
  };
}

interface FormFieldProviderProps {
  form: Form;
  formField: FormField;
  children: React.ReactNode;
}

const FormFieldProvider: FC<FormFieldProviderProps> = ({ form, formField, children }) => {
  const cfContext = useContext(CaseFormContext);
  const confirmCtx = useContext(ConfirmationDialogContext);
  const fieldHandle = cfContext.stateHandle[formField.id];
  const serverValuesForField = cfContext.serverValuesForField(formField.id);
  const state = fieldHandle !== undefined ? fieldHandle[0] : undefined;
  const setState = fieldHandle !== undefined ? fieldHandle[1] : undefined;
  const initialCellHandle = useState<InitialCell | undefined>(undefined);
  if (state === undefined || setState === undefined) {
    return <></>;
  }
  const groupedValueGroupStartDateHandle = useState<Date | undefined>(undefined);
  const groupedValueGroupEndDateHandle = useState<Date | undefined>(undefined);
  const valueGroupsHandle = objectPropertyHandle([state, setState], "valueGroups");
  const [valueGroups, setValueGroups] = valueGroupsHandle;
  const valueGroupIdToIndex = new Map(valueGroups.map((vg, index) => [vg.id, index]));
  const valueGroupHandle = (index: number) => arrayIndexHandle(valueGroupsHandle, index);
  const valueGroupValuesHandle = (index: number) => {
    const valueIndex = valueGroupHandle(index);
    if (valueIndex !== undefined) {
      return objectPropertyHandle(valueIndex, "values");
    }
  };
  const { enqueueSnackbar } = useSnackbar();
  const highestGroupIndex = valueGroups.length > 0 ? Math.max(...valueGroups.map((vg) => vg.id)) : 0;
  const validGroupByCells = formField.cells.filter((cell) => cell.type !== CellType.Date);
  const tags = useAppSelector(selectTags);
  const fieldTagConfigs = useAppSelector(selectFieldTagConfigs);
  const thisTagConfig = fieldTagConfigs.find((config) => config.fieldId === formField.id);

  // We count tags later (in useFilteredValueGroups), but we need to know if there's at least one now so we don't enable
  // a 0-count tag when filtering by default.
  const hasAtLeastOneOfTag: { [systemName: string]: boolean } = (serverValuesForField?.valueGroups || []).reduce(
    (map, vg) => {
      vg.tags.forEach((t) => (map[t.systemName] = true));
      return map;
    },
    {}
  );
  const filterStarredByDefault = thisTagConfig?.filteredByDefault?.includes(STARRED);
  const filterUntaggedByDefault = thisTagConfig?.filteredByDefault?.includes(UNTAGGED);
  const filteredByDefault = tags
    .filter((t) => t.filteredByDefaultFields.includes(formField.id) && hasAtLeastOneOfTag[t.systemName])
    .map((t) => t.systemName);
  if (filterStarredByDefault) filteredByDefault.push(STARRED);
  if (filterUntaggedByDefault) filteredByDefault.push(UNTAGGED);

  const tagHandle = useState<Set<string>>(new Set(filteredByDefault));

  const valueGroupValuesHandleForGroup = (groupId: number): StateHandle<FieldCellValueUI[]> | undefined => {
    const positionalIndex = valueGroupIdToIndex.get(groupId);

    return positionalIndex !== undefined ? valueGroupValuesHandle(positionalIndex) : undefined;
  };

  const valueGroupHandleForGroup = (groupId: number): StateHandle<FormFieldValueGroupUI> | undefined => {
    const positionalIndex = valueGroupIdToIndex.get(groupId);

    return positionalIndex !== undefined ? valueGroupHandle(positionalIndex) : undefined;
  };

  /** 👇 Queries about the state 👇 **/
  const hasFindings: boolean | undefined = cfContext.fieldHasFindingsMap[formField.id];
  const profileGenRunSummary = cfContext.fieldProfileGenRunsMap[formField.id];
  const hasValueGroupsFromAI = useMemo(() => valueGroups.some((vg) => vg.source?.sourceType === "llm"), [valueGroups]);
  const valueGroupsByDate: ValueGroupsByDate[] = useMemo(() => {
    const vgbd: ValueGroupsByDate[] = [];
    serverValuesForField?.valueGroups.forEach((vg) => {
      const dates = valueGroupDateValues(vg);
      dates.forEach((d) => {
        const found = vgbd.find((d2) => d2.date.getTime() === d.getTime());
        if (found === undefined) {
          vgbd.push({ date: d, valueGroups: [vg] });
        } else {
          found.valueGroups.push(vg);
        }
      });
    });
    return vgbd.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [serverValuesForField]);
  const cellByIdMap = useMemo(() => {
    const map = new Map<number, Cell>();
    formField.cells.forEach((c) => {
      map.set(c.id, c);
    });
    return map;
  }, [formField]);
  const fieldUIState = cfContext.fieldUIStateMapHandle[0]?.[formField.id];
  const setGroupExpanded = (expanded: boolean) => {
    cfContext.fieldUIStateMapHandle[1]((prev) =>
      produce(prev, (state) => {
        const ffState = state[formField.id];

        if (ffState) {
          ffState.groupExpanded = expanded;
        }
      })
    );
  };
  const expandGroupName = (groupName: string) => {
    setGroupExpanded(true);
    cfContext.fieldUIStateMapHandle[1]((prev) =>
      produce(prev, (state) => {
        const ffState = state[formField.id];

        if (ffState) {
          const expandedGroupNames = new Set(ffState.expandedGroupNames ?? []);
          expandedGroupNames.add(groupName);
          ffState.expandedGroupNames = [...expandedGroupNames];
        }
      })
    );
  };
  const collapseGroupName = (groupName: string) => {
    cfContext.fieldUIStateMapHandle[1]((prev) =>
      produce(prev, (state) => {
        const ffState = state[formField.id];

        if (ffState) {
          const expandedGroupNames = new Set(ffState.expandedGroupNames ?? []);
          expandedGroupNames.delete(groupName);
          ffState.expandedGroupNames = [...expandedGroupNames];
        }
      })
    );
  };
  const setFilterByStar = (filterValue: boolean) => {
    cfContext.fieldUIStateMapHandle[1]((prev) =>
      produce(prev, (state) => {
        const ffState = state[formField.id];

        if (ffState !== undefined) {
          ffState.filterByStarred = filterValue;
        }
      })
    );
  };
  const setGroupSort = (cellValue: string, cell?: Cell, sortDir?: "asc" | "desc") => {
    cfContext.fieldUIStateMapHandle[1]((prev) =>
      produce(prev, (state) => {
        const ffState = state[formField.id];

        if (ffState !== undefined) {
          if (cell !== undefined && sortDir !== undefined) {
            ffState.groupSorts[cellValue] = {
              cell,
              sortDir,
            };
          } else {
            delete ffState.groupSorts[cellValue];
          }
        }
      })
    );
  };
  const groupByCellId = fieldUIState?.groupByCellId ?? UNGROUPED_CELL_ID;
  const setGroupByCellId = (cellId: number) => {
    cfContext.fieldUIStateMapHandle[1]((prev) =>
      produce(prev, (state) => {
        const ffState = state[formField.id];

        if (ffState) {
          ffState.groupByCellId = cellId;
          ffState.groupSorts = {};
        }
      })
    );
  };
  const groupByCell = groupByCellId !== undefined ? cellByIdMap.get(groupByCellId) : undefined;
  const hasAnyDateCells = formField.cells.some((c) => c.type === CellType.Date);
  const hasAnyDateCellsWithValue = valueGroups.some((vg) => valueGroupDateValues(vg).length > 0);

  /** Value Group Positional Methods */
  const getValueGroupPosition = (groupId: number): number | undefined => {
    const idx = valueGroups.findIndex((vg) => vg.id === groupId);
    if (idx === -1) {
      return undefined;
    }
    return idx;
  };

  const isValueGroupFirst = (groupId: number) => {
    const position = getValueGroupPosition(groupId);
    return position !== undefined && position === 0;
  };

  const isValueGroupLast = (groupIndex: number) => {
    const position = getValueGroupPosition(groupIndex);
    return position !== undefined && position === valueGroups.length - 1;
  };

  const getNextValueGroup = (groupId: number): FormFieldValueGroupUI | undefined => {
    const position = getValueGroupPosition(groupId);
    if (position === undefined) {
      return undefined;
    }
    return valueGroups[position + 1];
  };
  /** End Value Group Positional Methods */

  const valueGroupById = (groupId: number): FormFieldValueGroupUI | undefined => {
    return valueGroups.find((vg) => vg.id === groupId);
  };

  const starredLocalValueGroups = valueGroups.filter(
    (vg) => vg.selectionStatus === FieldValueGroupSelectionStatus.Selected
  );

  const localStarFilteredValueGroups = fieldUIState?.filterByStarred ? starredLocalValueGroups : valueGroups;

  const dateCellIndex = formField.cells.findIndex((c) => c.key === CellKey.PrimaryDate);
  const lensSubtypeCellIndex = formField.cells.findIndex((c) => c.key === CellKey.LensSubtype);
  const nameCellIndex = formField.cells.findIndex((c) => c.key === CellKey.Name);
  const recordTypeCellIndex = formField.cells.findIndex((c) => c.key === CellKey.RecordType);
  const canAddValueGroup =
    (formField.repeating || valueGroups.length === 0) &&
    ((formField.promptYesNo && state.yesNoValue === true) || !formField.promptYesNo) &&
    valueGroups.every((vg) => !vg.isNew);

  /** 👇 Methods to mutate values groups and field state 👇 **/

  const setValueGroupNotes = (groupId?: number, notes?: string) => {
    setValueGroups((prev) =>
      produce(prev, (valueGroups) => {
        const vg = valueGroups.find((vg) => vg.id === groupId);
        if (vg !== undefined) {
          const old = vg.notes;
          vg.notes = notes;
          const meaningfulUpdate = Boolean(old) || Boolean(notes);
          if (meaningfulUpdate) {
            vg.touched = true;
          }
        }
      })
    );
  };

  // Force validation errors to be displayed, e.g. before saving if the field is untouched
  const setValueGroupErrors = (groupId: number): boolean => {
    let groupHasErrors = false;
    setValueGroups((prev) =>
      produce(prev, (valueGroups) => {
        const vg = valueGroups.find((vg) => vg.id === groupId);
        if (vg !== undefined) {
          vg.values.map((value, idx) => {
            const schema = formField.cells[idx];
            if (!schema) return false;
            const validationErrors = getCellValidationErrorsForValue(value, schema.validationConfig || {});
            const hasError = validationErrors.length > 0;
            value.error = hasError ? validationErrors[0] : undefined;
            groupHasErrors = groupHasErrors || hasError;
          });
        }
      })
    );
    return groupHasErrors;
  };

  const setValueGroupViewState = (groupId: number, viewState: RowViewState) => {
    const setViewState = (valueGroups: FormFieldValueGroupUI[]) => {
      const vg = valueGroups.find((vg) => vg.id === groupId);
      if (vg !== undefined) {
        vg.viewState = viewState;
      }
    };

    setValueGroups((prev) => produce(prev, setViewState));
    cfContext.updateValueGroups(formField.id, setViewState);
  };

  const setValueGroupViewStateBulk = (groupIds: number[], viewState: RowViewState) => {
    const setViewState = (valueGroups: FormFieldValueGroupUI[]) => {
      const vgs = valueGroups.filter((vg) => groupIds.includes(vg.id));
      vgs.forEach((vg) => {
        vg.viewState = viewState;
      });
    };

    setValueGroups((prev) => produce(prev, setViewState));
    cfContext.updateValueGroups(formField.id, setViewState);
  };

  const setValueGroupTags = (id: number, tags: FieldValueGroupTag[]) => {
    const setTags = (valueGroups: FormFieldValueGroupUI[]) => {
      const vg = valueGroups.find((vg) => vg.id === id);
      if (!vg) return;
      vg.tags = tags;
    };

    setValueGroups((prev) => produce(prev, setTags));
    cfContext.updateValueGroups(formField.id, setTags);
  };

  const selectValueGroups = async (groupIds: number[], action: FieldValueGroupSelectionStatus) => {
    try {
      cfContext.mutationInProgressHandle[1](true);
      await runGqlMutation(UpdateValueGroupInfoBulkDocument, {
        caseId: cfContext.caseId,
        formId: form.id,
        fieldId: formField.id,
        valueGroupInfos: groupIds.map((groupId) => ({
          groupIndex: groupId,
          selectionStatus: action,
        })),
      });

      const setSelectionStatus =
        (resetFilter: (willHaveStars: boolean) => void) => (valueGroups: FormFieldValueGroupUI[]) => {
          const willHaveStars =
            valueGroups
              .filter((valueGroup) => valueGroup.selectionStatus === FieldValueGroupSelectionStatus.Selected)
              .filter((valueGroup) => !groupIds.includes(valueGroup.id)).length > 0;
          const vgs = valueGroups.filter((valueGroup) => groupIds.includes(valueGroup.id));
          vgs.forEach((vg) => {
            vg.selectionStatus = action;
          });
          resetFilter(willHaveStars);
        };

      const setThisSelectionStatus = setSelectionStatus((willHaveStars) => {
        if (fieldUIState?.filterByStarred && !willHaveStars) {
          setFilterByStar(false);
        }
      });
      const setCfSelectionStatus = setSelectionStatus((_) => {});

      setValueGroups((prev) => produce(prev, setThisSelectionStatus));
      cfContext.updateValueGroups(formField.id, setCfSelectionStatus);
    } catch (e) {
      console.error(e);
      const verb =
        action === FieldValueGroupSelectionStatus.Selected
          ? "star"
          : action === FieldValueGroupSelectionStatus.Archived
            ? "archive"
            : "unstar";
      enqueueSnackbar(`Unable to ${verb} response`, { variant: "error" });
    } finally {
      cfContext.mutationInProgressHandle[1](false);
    }
  };

  // initial cell means we're adding a value group from the group by cell, make sure it's also on
  // case form context since CFC creates the groups.
  const addValueGroup = (initialCell?: InitialCell) => {
    if (canAddValueGroup) {
      const newGroup = emptyValueGroup(formField, highestGroupIndex + 1, undefined, initialCell);
      setState(
        produce((formFieldValuesUI) => {
          formFieldValuesUI.valueGroups.push(newGroup);
        })
      );
      if (initialCell) {
        initialCellHandle[1](initialCell);
        cfContext.updateValueGroups(formField.id, (valueGroups) => {
          valueGroups.push(newGroup);
        });
      }
    }
  };

  const removeReference = async (groupId: number, referenceId: number) => {
    try {
      cfContext.mutationInProgressHandle[1](true);
      await runGqlMutation(DeleteReferencesDocument, {
        caseId: cfContext.caseId,
        formId: form.id,
        referenceIds: [referenceId],
      });
      const removeReference = (valueGroups: FormFieldValueGroupUI[]) => {
        const vg = valueGroups.find((vg) => vg.id === groupId);
        if (vg === undefined) {
          return;
        }
        const referenceIndex = vg.references.findIndex((r) => r.id === referenceId);
        if (referenceIndex === -1) {
          return;
        }
        vg.references?.splice(referenceIndex, 1);
      };

      setValueGroups((prev) => produce(prev, removeReference));
      cfContext.updateValueGroups(formField.id, removeReference);
      enqueueSnackbar("Reference deleted", { variant: "success" });
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to remove reference", { variant: "error" });
    } finally {
      cfContext.mutationInProgressHandle[1](false);
    }
  };

  const __removeValueGroup = async (groupIds: number[]): Promise<void> => {
    try {
      cfContext.mutationInProgressHandle[1](true);
      await runGqlMutation(DeleteValueGroupsDocument, {
        formId: form.id,
        caseId: cfContext.caseId,
        fieldId: formField.id,
        valueGroupIndices: groupIds,
        deleteAll: false,
      });

      const removeValueGroupInner = (valueGroups: FormFieldValueGroupUI[]) => {
        groupIds.forEach((groupId) => {
          const positionalIndex = valueGroups.findIndex((vg) => vg.id === groupId);
          if (positionalIndex !== -1) {
            valueGroups.splice(positionalIndex, 1);
          }
        });
      };
      setState(
        produce((formFieldValuesUI) => {
          removeValueGroupInner(formFieldValuesUI.valueGroups);
        })
      );
      cfContext.updateValueGroups(formField.id, removeValueGroupInner);
      enqueueSnackbar(pluralize(groupIds.length, "Response deleted", "Responses deleted"), { variant: "success" });
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to delete response", { variant: "error" });
    } finally {
      cfContext.mutationInProgressHandle[1](false);
      initialCellHandle[1](undefined);
    }
  };

  const removeValueGroups = (
    groupIds: number[],
    callback: () => void,
    popover?: UsePopover,
    groupName?: string
  ): void => {
    confirmCtx.confirm({
      title: `Delete ${pluralize(groupIds.length, "Response", `${groupIds.length} Responses`)}`,
      prompt: (
        <P variant="sm">
          Are you sure you want to delete{" "}
          {pluralize(groupIds.length, `this ${groupName ?? ""} response`, `these ${groupName ?? ""} responses`)}?
        </P>
      ),
      callback: async () => {
        await __removeValueGroup(groupIds);
        return callback();
      },
      cancelButtonProps: {
        color: "default",
      },
      confirmButtonProps: {
        color: "error",
        variant: "outlined",
      },
      confirmButtonText: "Delete",
      usePopover: popover,
    });
  };

  const removeValueGroup = (groupId: number, callback: () => void, popover?: UsePopover): void => {
    const group = valueGroupById(groupId);
    // delete on an unsaved value group can skip the confirmation
    if (group?.isNew) {
      const innerRemoveNew = (valueGroups: FormFieldValueGroupUI[]) => {
        const positionalIndex = valueGroups.findIndex((vg) => vg.id === groupId);
        if (positionalIndex !== -1) {
          valueGroups.splice(positionalIndex, 1);
        }
      };
      setState(produce((formFieldValuesUI) => innerRemoveNew(formFieldValuesUI.valueGroups)));
      cfContext.updateValueGroups(formField.id, innerRemoveNew);
      popover?.onPopoverClose();
      callback();
    } else {
      removeValueGroups([groupId], callback, popover);
    }
  };

  const addReferenceToValueGroup = async (
    groupId: number,
    intakeId: number,
    intakePage: number,
    block: Block,
    pageRangeStart?: number,
    pageRangeEnd?: number
  ) => {
    try {
      cfContext.mutationInProgressHandle[1](true);
      const referenceId = await runGqlMutation(UpsertValueGroupReferenceDocument, {
        caseId: cfContext.caseId,
        formId: form.id,
        fieldId: formField.id,
        groupIndex: groupId,
        intakeId,
        intakePage,
        blockIndex: block.index,
        pageRangeEnd,
        pageRangeStart,
      }).then((res) => res.case.form.field.upsertValueGroupReferenceV2);
      const addReference = (valueGroups: FormFieldValueGroupUI[]) => {
        const valueGroup = valueGroups.find((vg) => vg.id === groupId);

        if (valueGroup !== undefined) {
          valueGroup.references.push({
            id: referenceId,
            documentIntakeId: intakeId,
            originalPage: intakePage,
            blockIndex: block.index,
            blockPage: block.page,
            blockText: block.text,
            documentId: block.documentId,
            documentName: block.documentName,
            valueGroupId: groupId,
            pageRangeStart,
            pageRangeEnd,
          });
        }
      };
      setValueGroups((prev) => produce(prev, addReference));
      cfContext.updateValueGroups(formField.id, addReference);
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to add reference", { variant: "error" });
    } finally {
      cfContext.mutationInProgressHandle[1](false);
    }
  };

  const updateReference = async (body: {
    groupId: number;
    reference: FieldValueGroupReference;
    pageRangeStart?: number;
    pageRangeEnd?: number;
  }) => {
    try {
      cfContext.mutationInProgressHandle[1](true);
      await runGqlMutation(UpsertValueGroupReferenceDocument, {
        caseId: cfContext.caseId,
        formId: form.id,
        referenceId: body.reference.id,
        fieldId: formField.id,
        groupIndex: body.groupId,
        pageRangeStart: body.pageRangeStart,
        pageRangeEnd: body.pageRangeEnd,
        intakeId: body.reference.documentIntakeId,
        intakePage: body.reference.originalPage,
        blockIndex: body.reference.blockIndex,
      });
      const updateReference = (valueGroups: FormFieldValueGroupUI[]) => {
        valueGroups.forEach((vg) => {
          const reference = vg.references.find((r) => r.id === body.reference.id);
          if (reference !== undefined) {
            reference.pageRangeStart = body.pageRangeStart;
            reference.pageRangeEnd = body.pageRangeEnd;
            reference.documentIntakeId = body.reference.documentIntakeId;
            reference.originalPage = body.reference.originalPage;
            reference.blockIndex = body.reference.blockIndex;
          }
        });
      };
      setValueGroups((prev) => produce(prev, updateReference));
      cfContext.updateValueGroups(formField.id, updateReference);
    } catch (e) {
      enqueueSnackbar("Unable to edit reference", { variant: "error" });
    } finally {
      cfContext.mutationInProgressHandle[1](false);
    }
  };

  const validateValueGroup = (valueGroup: FormFieldValueGroupUI): boolean => {
    const cells = formField.cells;
    const result = valueGroup.values.reduce((validSoFar, cellValue, idx) => {
      const thisCellConfig = cells[idx]?.validationConfig;
      return validSoFar && validateCellValue(cellValue, thisCellConfig || {});
    }, true);
    return result;
  };

  const saveValueGroup = async (groupId: number, returnToViewState?: RowViewState): Promise<boolean | undefined> => {
    try {
      cfContext.mutationInProgressHandle[1](true);
      const valueGroup = valueGroupById(groupId);
      if (valueGroup === undefined || !nonEmpty(valueGroup.values) || !validateValueGroup(valueGroup)) {
        return false;
      }
      const yesNoValue = formField.promptYesNo ? state.yesNoValue : true;
      const existingIndex = valueGroup.isNew ? undefined : valueGroup.id;
      const cells = valueGroup.values.map((v) => v.value);
      const valueGroupToSend: ValueGroupToAddInput = {
        existingIndex,
        rank: existingIndex !== undefined ? valueGroup.rank : undefined,
        blockIntakeReferences: [],
        cells,
        notes: valueGroup.notes,
        selectionStatus: valueGroup.selectionStatus,
      };

      const result = await runGqlMutation(UpsertFullValueGroupsDocument, {
        formId: form.id,
        caseId: cfContext.caseId,
        fieldId: formField.id,
        yesNoValue,
        valueGroups: [valueGroupToSend],
      }).then((res) => res.case.form.field.upsertFullValueGroups);

      const handleValueGroups = (valueGroups: FormFieldValueGroupUI[]) => {
        if (valueGroup.isNew) {
          const vg = valueGroups.find((vg) => vg.isNew);
          const serverVG = result[0];
          if (vg !== undefined && serverVG !== undefined && serverVG !== null) {
            vg.id = serverVG.localGroupId;
            vg.rank = serverVG.rank;
            vg.values = valueGroup.values;
            vg.notes = valueGroup.notes;
            vg.isNew = false;
            vg.touched = false;
            vg.responseNumber = valueGroups.length;
            if (returnToViewState) {
              vg.viewState = returnToViewState;
            }
            vg.serialId = serverVG.valueGroupSerialId;
            vg.availableTags = cfContext.fieldTagMapHandle[0].get(vg.fieldId) || [];
          }
        } else {
          const vg = valueGroups.find((vg) => vg.id === valueGroup.id);
          if (vg !== undefined) {
            vg.values = valueGroup.values;
            vg.notes = valueGroup.notes;
            vg.touched = false;
            if (returnToViewState) {
              vg.viewState = returnToViewState;
            }
          }
        }
      };

      setState((prev) =>
        produce(prev, (field) => {
          field.yesNoValue = yesNoValue;
          handleValueGroups(field.valueGroups);
        })
      );

      if (valueGroup.isNew && serverValuesForField?.valueGroups.find((vg) => vg.id === valueGroup.id) === undefined) {
        cfContext.updateValueGroup(formField.id, yesNoValue, (valueGroups: FormFieldValueGroupUI[]) => {
          valueGroups.push(valueGroup);
        });
      }
      cfContext.updateValueGroup(formField.id, yesNoValue, handleValueGroups);
      if (valueGroup.isNew && groupByCell !== undefined && initialCellHandle[0] !== undefined) {
        const cellValue = cellValueFromGQL(valueGroup.values.find((v) => v.cellId === groupByCell.id)?.value);
        if (cellValue !== undefined && cellValue !== cellValueFromGQL(initialCellHandle[0].value)) {
          enqueueSnackbar(
            `${groupByCell.name} - ${truncateString(displaySimpleCellValueString(cellValue), 26)} response added`,
            {
              variant: "success",
            }
          );
        }
      }

      return !!result;
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to update", { variant: "error" });
    } finally {
      cfContext.mutationInProgressHandle[1](false);
      initialCellHandle[1](undefined);
    }
  };

  const __saveYesNo = async (yesNo: boolean | undefined) => {
    try {
      cfContext.mutationInProgressHandle[1](true);
      await runGqlMutation(UpsertFullValueGroupsDocument, {
        formId: form.id,
        caseId: cfContext.caseId,
        fieldId: formField.id,
        yesNoValue: yesNo,
      });
      if (yesNo === false && !empty(valueGroups)) {
        await runGqlMutation(DeleteValueGroupsDocument, {
          formId: form.id,
          caseId: cfContext.caseId,
          fieldId: formField.id,
          valueGroupIndices: [],
          deleteAll: true,
        });
        cfContext.setFieldYesNoToFalse(formField.id, (field) => {
          field.yesNoValue = false;
          field.valueGroups = [];
        });
        setValueGroups([]);
      }
      setState((prev) =>
        produce(prev, (field) => {
          field.yesNoValue = yesNo;
        })
      );
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to update", { variant: "error" });
    } finally {
      cfContext.mutationInProgressHandle[1](false);
    }
  };

  const saveYesNo = (v: boolean | undefined) => {
    const savedValueGroups = state.valueGroups.filter((vg) => !vg.isNew);
    if (v === false && !empty(savedValueGroups)) {
      confirmCtx.confirm({
        title: "Are you sure?",
        prompt: (
          <P variant="body2">
            Setting to <b>No</b> will delete <b>{savedValueGroups.length}</b>{" "}
            {pluralize(savedValueGroups.length, "response")}
          </P>
        ),
        callback: () => __saveYesNo(v),
      });
    } else {
      __saveYesNo(v);
    }
  };

  const swapValueGroups = async (evt: MouseEvent, groupId: number, dir: "up" | "down"): Promise<void> => {
    try {
      cfContext.mutationInProgressHandle[1](true);
      evt.stopPropagation();
      const idx1 = valueGroups.findIndex((vg) => vg.id === groupId);

      if (idx1 === -1) {
        return;
      }

      const disambiguateAndSwap = (
        rank: number,
        firstGroupId: number,
        secondGroupId: number
      ): UpdateFormCaseValueGroupArgsInput[] => {
        // Get all the value groups with this same rank
        const rankGroup = valueGroups.filter((vg) => vg.rank === rank);
        if (rankGroup.length === 0) return [];

        const step = 1 / rankGroup.length;

        // Spread them out evenly between rank and rank + 1
        const disambiguated = rankGroup.map((vg, index) => {
          return { groupIndex: vg.id, rank: vg.rank + step * index };
        });

        // Now do the actual swap we wanted to do
        const firstGroupIndex = disambiguated.findIndex((g) => g.groupIndex === firstGroupId);
        const secondGroupIndex = disambiguated.findIndex((g) => g.groupIndex === secondGroupId);
        const firstItem = disambiguated[firstGroupIndex];
        const secondItem = disambiguated[secondGroupIndex];
        if (!firstItem || !secondItem) return [];
        [firstItem.rank, secondItem.rank] = [secondItem.rank, firstItem.rank];
        return disambiguated;
      };

      const sortValueGroups = (
        valueGroups: FormFieldValueGroupUI[],
        valueGroupUpdates: { groupId: number; rank: number }[]
      ) => {
        valueGroupUpdates.forEach((vgu) => {
          const vg = valueGroups.find((vg2) => vg2.id === vgu.groupId);
          if (vg !== undefined) {
            vg.rank = vgu.rank;
          }
        });
        valueGroups.sort((a, b) => {
          if (a.rank === b.rank) return a.id - b.id;
          return a.rank - b.rank;
        });
        valueGroups.forEach((vg, idx) => {
          vg.responseNumber = idx + 1;
        });
      };

      const group1 = valueGroups[idx1];
      const group2 = dir === "up" ? valueGroups[idx1 - 1] : valueGroups[idx1 + 1];

      if (group1 !== undefined && group2 !== undefined) {
        const sameRank = group1.rank === group2.rank;
        const valueGroupInfos = sameRank
          ? disambiguateAndSwap(group1.rank, group1.id, group2.id)
          : [
              { groupIndex: group1.id, rank: sameRank ? group2.rank : group2.rank },
              { groupIndex: group2.id, rank: group1.rank },
            ];
        await runGqlMutation(UpdateValueGroupInfoBulkDocument, {
          caseId: cfContext.caseId,
          formId: form.id,
          fieldId: formField.id,
          valueGroupInfos,
        });
        const valueGroupUpdates = valueGroupInfos.map((vg) => ({ groupId: vg.groupIndex, rank: vg.rank || 0 }));
        cfContext.sortValueGroups(formField.id, valueGroupUpdates, sortValueGroups);
        setValueGroups((prev) =>
          produce(prev, (valueGroups) => {
            sortValueGroups(valueGroups, valueGroupUpdates);
          })
        );
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to change order", { variant: "error" });
    } finally {
      cfContext.mutationInProgressHandle[1](false);
    }
  };

  const sortResponses = async (cellId: number, order: "ascending" | "descending"): Promise<void> => {
    try {
      const descending = order === "descending";
      const results = await runGqlMutation(SortFieldValueGroupsDocument, {
        formId: form.id,
        caseId: cfContext.caseId,
        fieldId: formField.id,
        sortByCellId: cellId,
        descending,
      }).then((res) => res.case.form.field.sortFieldValueGroupsV2);
      const sortValueGroups = (fieldValuesUI: FormFieldValuesUI, fieldValueGroupIds: FieldValueGroupIds[]) => {
        fieldValuesUI.valueGroups.forEach((vg) => {
          const foundValueGroupId = fieldValueGroupIds.find((vgi) => vgi.localGroupId === vg.id);
          if (foundValueGroupId !== undefined) {
            vg.rank = foundValueGroupId.rank;
          }
        });
        fieldValuesUI.valueGroups.sort((a, b) => a.rank - b.rank);
      };

      setState((prev) => produce(prev, (fieldValuesUI) => sortValueGroups(fieldValuesUI, results)));
      cfContext.sortValueGroupsFromServerSort(formField.id, results, sortValueGroups);
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to sort", { variant: "error" });
    } finally {
      cfContext.mutationInProgressHandle[1](false);
    }
  };

  const loadHasFindings = () => {
    if (hasFindings === undefined) {
      return cfContext.loadFieldHasFindings(formField.id);
    } else {
      return Promise.resolve();
    }
  };

  const formFieldOperations: FormFieldOperations = {
    form,
    formField,
    cellByIdMap,
    hasValueGroupsFromAI,
    state,
    valueGroupById,
    addValueGroup,
    saveValueGroup,
    saveYesNo,
    swapValueGroups,
    sortResponses,
    valueGroups: valueGroupsHandle[0],
    localStarFilteredValueGroups,
    valueGroupValuesHandle,
    loadHasFindings: loadHasFindings,
    hasFindings,
    profileGenRunSummary,
    dateCellIndex,
    lensSubtypeCellIndex,
    nameCellIndex,
    recordTypeCellIndex,
    setFilterByStar,
    setValueGroupNotes,
    setValueGroupErrors,
    setValueGroupViewState,
    setValueGroupViewStateBulk,
    removeReference,
    updateReference,
    addReferenceToValueGroup,
    removeValueGroup,
    removeValueGroups,
    selectValueGroups,
    groupByCellIdHandle: [groupByCellId, setGroupByCellId],
    groupByCell,
    groupedValueGroupStartDateHandle,
    groupedValueGroupEndDateHandle,
    valueGroupsByDate,
    validGroupByCells,
    hasAnyDateCells,
    hasAnyDateCellsWithValue,
    getValueGroupPosition,
    isValueGroupFirst,
    isValueGroupLast,
    valueGroupValuesHandleForGroup,
    valueGroupHandleForGroup,
    getNextValueGroup,
    canAddValueGroup,
    fieldUIState,
    setGroupExpanded,
    setGroupSort,
    expandGroupName,
    collapseGroupName,
    setValueGroupTags,
    tagHandle,
  };
  return <FormFieldContext.Provider value={formFieldOperations}>{children}</FormFieldContext.Provider>;
};

export default FormFieldProvider;
